import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useHybridCart } from "@/contexts/HybridCartContext";
import { pixelInitiateCheckout } from "@/lib/pixel";
import { gtmEvent } from "@/lib/gtm";
import { firePurchaseOnce } from "@/lib/purchase";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { CheckoutAuthChoice } from "@/components/checkout/CheckoutAuthChoice";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import orderService from "@/services/orderService";
import { ShoppingBag, CheckCircle, Loader2, Download } from "lucide-react";
import { KYCCheckoutForm } from "@/components/checkout/KYCCheckoutForm";
import { CardPaymentForm } from "@/components/checkout/CardPaymentForm";
import { CheckoutStepper } from "@/components/checkout/CheckoutStepper";
import { deriveSteps, type CheckoutStep } from "@/lib/checkoutSteps";

interface OrderItem {
  name?: string;
  quantity?: number;
  customization?: { personalizedName?: string };
}

interface PurchasedItem {
  name: string;
  quantity: number;
  personalizedName?: string;
}

interface OrderResult {
  success: boolean;
  order: {
    id: string;
    orderNumber: string;
    total: number;
    status: string;
    paymentInfo?: {
      status: string;
      transactionId?: string;
      method: string;
    };
    createdAt: string;
    items?: OrderItem[];
  };
  nextSteps: string[];
  paymentSuccess?: boolean;
  message?: string;
  // Legacy support
  orderId?: string;
  paymentUrl?: string;
}

const HybridCheckout = () => {
  // --- HOOKS (MUST BE CALLED UNCONDITIONALLY) ---
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>("user-form");
  const [cardOrderId, setCardOrderId] = useState<string>("");
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>(
    undefined
  );
  const [onPaymentSubStep, setOnPaymentSubStep] = useState(false);
  const [purchasedItems, setPurchasedItems] = useState<PurchasedItem[]>([]);
  const [orderResult, setOrderResult] = useState(
    /* <OrderResult | null> */ null
  );
  const [guestEmail, setGuestEmail] = useState("");
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const { toast } = useToast();
  const {
    cart,
    hasItems,
    mergeWithUserCart,
    loading,
    clearCartSilently,
    getCartItems,
  } = useHybridCart();
  const navigate = useNavigate();

  // Scroll automático al top cuando cambia el step
  useScrollToTop({ trigger: currentStep });

  // --- EFFECTS ---

  // Effect to redirect if cart is empty *after* loading is complete (except on success step)
  useEffect(() => {
    if (!loading && !hasItems && currentStep !== "success" && !orderResult) {
      navigate("/cart");
    }
  }, [loading, hasItems, navigate, currentStep, orderResult]);

  // No longer needed - we start directly at user-form
  // Users (guest or authenticated) fill out the same form

  useEffect(() => {
    console.log("🔄 currentStep →", currentStep);
  }, [currentStep]);

  // Analytics: InitiateCheckout (Pixel) + begin_checkout (GA4) al llegar con items
  useEffect(() => {
    if (loading || !hasItems || !cart) return;
    pixelInitiateCheckout({
      value: cart.subtotal ?? 0,
      num_items: cart.totalItems ?? 0,
    });
    gtmEvent("begin_checkout", {
      currency: "USD",
      value: cart.subtotal ?? 0,
    });
  }, [loading]);

  useEffect(() => {
    console.log("orderResult:", orderResult);
  }, [orderResult]);

  // --- HELPERS ---
  // Note: The "auth" step has been removed. All users (guest or authenticated)
  // go directly to the user-form step. The form will handle both cases.

  const getSuccessMessage = (paymentMethod: string, orderNumber: string) => {
    switch (paymentMethod) {
      case "whatsapp":
        return `Tu orden #${orderNumber} ha sido creada. Se ha abierto WhatsApp para que confirmes tu pedido con nosotros.`;
      case "bank_transfer":
        return `Tu orden #${orderNumber} ha sido creada. Te contactaremos pronto con los detalles para realizar la transferencia bancaria.`;
      case "cash_on_delivery":
        return `Tu orden #${orderNumber} ha sido creada. Te contactaremos para coordinar la entrega y el pago contra entrega.`;
      case "paguelofacil":
      default:
        return `Tu orden #${orderNumber} ha sido creada y el pago fue procesado exitosamente.`;
    }
  };

  const getNextSteps = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "whatsapp":
        return [
          "Confirma tu pedido por WhatsApp con nuestro equipo",
          "Te proporcionaremos los detalles de pago y envío",
          "Coordinamos la entrega según tu ubicación",
          "Recibirás actualizaciones del estado de tu pedido",
        ];
      case "bank_transfer":
        return [
          "Te enviaremos un email con los datos bancarios",
          "Realiza la transferencia dentro de 24 horas",
          "Envía el comprobante a nuestro WhatsApp",
          "Procesaremos tu pedido una vez confirmado el pago",
        ];
      case "cash_on_delivery":
        return [
          "Te contactaremos para coordinar la entrega",
          "Prepara el monto exacto para el pago",
          "El pedido se entregará en la dirección indicada",
          "El pago se realiza al momento de la entrega",
        ];
      case "paguelofacil":
      default:
        return [
          "Te enviaremos confirmación por email",
          "Procesaremos tu pedido inmediatamente",
          "Recibirás actualizaciones del estado de envío",
        ];
    }
  };

  // --- HANDLERS ---

  /* // No longer needed, auth happens before reaching this page
  const handleAuthSuccess = async () => {
    const proceedToNextStep = () => setCurrentStep("user-form");

    if (cart?.isGuest && cart.items.length > 0) {
      try {
        await mergeWithUserCart(proceedToNextStep);
      } catch (error) {
        console.error("Failed to merge carts:", error);
        proceedToNextStep(); 
      }
    } else {
      proceedToNextStep();
    }
  };
  */

  const handleFormSuccess = async (result) => {
    console.log("handleFormSuccess result:", result);

    setOrderResult(result);

    setGuestEmail(
      result?.guestEmail ||
        result?.data?.order?.guestInfo?.email ||
        result?.order?.guestInfo?.email ||
        ""
    );

    // Verificar método de pago para decidir el siguiente paso
    // El backend devuelve la estructura: { data: { order: { paymentMethod } } }
    const paymentMethod =
      result.data?.order?.paymentMethod ||
      result.order?.paymentMethod ||
      result.paymentMethod;

    console.log("Payment method detected:", paymentMethod);

    // Tarjeta (cubopago): pasar al formulario de tarjeta, aún no hay compra
    if (paymentMethod === "cubopago") {
      const orderId =
        result.data?.order?.id ||
        result.order?.id ||
        result.orderId ||
        "";
      setCardOrderId(orderId);
      setCurrentStep("card-form");
      return;
    }

    // Todos los demás métodos van directo a success (whatsapp, bank_transfer, cash_on_delivery).
    // Fijar el paso antes de vaciar el carrito evita que el effect de carrito vacío
    // desmonte la confirmación.
    setCurrentStep("success");

    // Analytics: Purchase (Pixel) + purchase (GA4), una sola vez por orden.
    // El backend identifica la orden con orderNumber (no siempre manda id).
    const orderId =
      result.data?.order?.orderNumber ||
      result.order?.orderNumber ||
      result.order?.id ||
      result.orderId ||
      "";
    const orderTotal =
      result.data?.order?.total ?? result.order?.total ?? result.amount ?? 0;
    firePurchaseOnce({
      orderId,
      value: orderTotal,
      paymentType: paymentMethod,
    });

    setPurchasedItems(
      getCartItems().map((item) => ({
        name: "productId" in item ? item.name : item.product.name,
        quantity: item.quantity,
        personalizedName: item.customization?.personalizedName,
      }))
    );

    // Vaciar el carrito después de fijar el paso para no disparar el redirect.
    await clearCartSilently();
  };

  const handleCheckoutSuccess = async (result /* result: OrderResult */) => {
    console.log("handleCheckoutSuccess exec");
    console.log({ result });

    // Actualizar orderResult con la nueva información del pago
    setOrderResult(result);

    // El paso avanza a "success" antes de vaciar el carrito: el effect de carrito
    // vacío no debe desmontar la confirmación recién pagada.
    setCurrentStep("success");

    // Analytics: compra con tarjeta aprobada (esta ruta no medía Purchase)
    firePurchaseOnce({
      orderId:
        cardOrderId ||
        result.data?.order?.orderNumber ||
        result.order?.orderNumber ||
        result.order?.id ||
        result.orderId ||
        "",
      value:
        result.data?.order?.total ??
        result.order?.total ??
        result.data?.amount ??
        result.amount ??
        0,
      paymentType: "cubopago",
    });

    setPurchasedItems(
      getCartItems().map((item) => ({
        name: "productId" in item ? item.name : item.product.name,
        quantity: item.quantity,
        personalizedName: item.customization?.personalizedName,
      }))
    );

    // Vaciar el carrito después de fijar el paso para no disparar el redirect.
    await clearCartSilently();
  };

  const receiptOrderId = orderResult?.order?.id || orderResult?.orderId;
  const isPaid =
    orderResult?.paymentSuccess === true ||
    orderResult?.order?.paymentInfo?.status === "completed";
  const canDownloadReceipt = Boolean(
    receiptOrderId && isPaid && (guestEmail || isAuthenticated)
  );

  const handleDownloadReceipt = async () => {
    if (!receiptOrderId) return;
    try {
      setDownloadingReceipt(true);
      // El checkout crea órdenes guest aunque haya sesión: manda la identidad
      // de la orden (su email), no la sesión del navegador.
      await orderService.downloadReceipt(
        receiptOrderId,
        guestEmail ? { guestEmail } : undefined
      );
    } catch {
      toast({
        title: "No se pudo descargar el recibo",
        variant: "destructive",
      });
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const handleBackToAuth = () => {
    /* setCurrentStep("auth"); */
    navigate("/cart");
  };

  const handleSubStepChange = useCallback(
    (info: { name: string }) => setOnPaymentSubStep(info.name === "pago-notas"),
    []
  );

  // --- RENDER LOGIC ---

  const renderContent = () => {
    const progressSteps = deriveSteps(
      selectedMethod,
      currentStep,
      onPaymentSubStep
    );
    const successfulItems =
      orderResult?.order?.items && orderResult.order.items.length > 0
        ? orderResult.order.items.map((item) => ({
            name: item.name || "",
            quantity: item.quantity || 0,
            personalizedName: item.customization?.personalizedName,
          }))
        : purchasedItems;

    // 1. Show loading spinner if authenticated and cart is loading
    if (loading && isAuthenticated) {
      return (
        <div className="flex flex-col items-center justify-center text-center py-20">
          <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
          <h2 className="text-xl font-bold text-brown-dark">
            Cargando tu carrito...
          </h2>
          <p className="text-brown">Estamos preparando todo para ti.</p>
        </div>
      );
    }

    // 2. Show empty cart message if loading is done and no items
    /* if (!hasItems) {
      return (
        <div className="min-h-screen bg-[#FEFDFD] pb-12">
          <div className="container mx-auto px-4 max-w-md text-center">
            <Card>
              <CardContent className="pt-8 pb-6">
                <ShoppingBag className="w-16 h-16 mx-auto text-brown/50 mb-4" />
                <h2 className="text-xl font-bold text-brown-dark mb-2">
                  Tu carrito está vacío
                </h2>
                <p className="text-brown mb-6">
                  Agrega productos para continuar con el checkout
                </p>
                <Button
                  onClick={() => navigate("/products")}
                  className="w-full"
                >
                  Ver productos
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    } */

    // 3. Show main checkout flow
    return (
      <>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brown-dark mb-2">Checkout</h1>
          {currentStep !== "success" && (
            <div className="flex items-center justify-center gap-2 text-brown">
              <ShoppingBag className="w-5 h-5" />
              <span>
                {cart?.totalItems || 0} productos • USD{" "}
                {(cart?.subtotal || 0).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        <div className="max-w-md mx-auto mb-8">
          <CheckoutStepper steps={progressSteps} />
        </div>

        {/* Step Content */}
        {/* {currentStep === "auth" && (
          <CheckoutAuthChoice
            onGuestCheckout={handleGuestCheckout}
            onAuthSuccess={handleAuthSuccess}
          />
        )} */}

        {currentStep !== "success" && (
          <KYCCheckoutForm
            active={currentStep === "user-form"}
            onBack={handleBackToAuth}
            onSuccess={handleFormSuccess}
            onMethodChange={setSelectedMethod}
            onSubStepChange={handleSubStepChange}
          />
        )}

        {currentStep === "card-form" && (
          <CardPaymentForm
            orderId={cardOrderId}
            onBack={() => setCurrentStep("user-form")}
            onSuccess={handleCheckoutSuccess}
          />
        )}

        {currentStep === "success" && orderResult && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="pt-8 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
                <h2 className="text-2xl font-bold text-brown-dark mb-2">
                  ¡Pedido realizado exitosamente!
                </h2>
                <p className="text-brown mb-6">
                  {orderResult.message ||
                    getSuccessMessage(
                      orderResult.order?.paymentMethod || orderResult.paymentMethod || "paguelofacil",
                      orderResult.order?.orderNumber || orderResult.orderNumber || ""
                    )}
                </p>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-brown">Total:</span>
                      <span className="font-bold ml-2">
                        $
                        {(
                          orderResult.order?.total ||
                          orderResult.amount ||
                          0
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-brown">Estado:</span>
                      <span className="font-bold ml-2 capitalize">
                        {orderResult.order?.status || "pendiente"}
                      </span>
                    </div>
                    {orderResult.order?.paymentInfo?.transactionId && (
                      <div className="col-span-2 pt-2 border-t border-gray-200">
                        <span className="text-brown">Transacción:</span>
                        <span className="font-mono text-xs ml-2">
                          {orderResult.order?.paymentInfo?.transactionId}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {successfulItems.length > 0 && (
                  <div className="text-left mb-6">
                    <h3 className="font-bold text-brown-dark mb-2">
                      Tu pedido:
                    </h3>
                    <ul className="space-y-1 text-sm text-brown">
                      {successfulItems.map((item, index) => (
                        <li
                          key={`${item.name}-${index}`}
                          className="flex items-start justify-between gap-2"
                        >
                          <span>
                            {item.name}
                            {item.personalizedName
                              ? ` (${item.personalizedName})`
                              : ""}
                          </span>
                          <span className="text-brown/70">
                            ×{item.quantity}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="text-left mb-6">
                  <h3 className="font-bold text-brown-dark mb-2">
                    Próximos pasos:
                  </h3>
                  <ul className="space-y-1 text-sm text-brown">
                    {getNextSteps(
                      orderResult.order?.paymentMethod || orderResult.paymentMethod || "paguelofacil"
                    ).map((step, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">•</span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>

                {canDownloadReceipt && (
                  <Button
                    variant="outline"
                    className="w-full mb-4"
                    onClick={handleDownloadReceipt}
                    disabled={downloadingReceipt}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingReceipt ? "Generando..." : "Descargar recibo"}
                  </Button>
                )}

                <div className="flex gap-4">
                  <Button
                    onClick={() => navigate("/products")}
                    variant="outline"
                    className="flex-1"
                  >
                    Seguir comprando
                  </Button>
                  <Button onClick={() => navigate("/")} className="flex-1">
                    Ir al inicio
                  </Button>
                </div>

                {/* {orderResult.paymentUrl && (
                  <div className="mt-4">
                    <Button
                      asChild
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <a
                        href={orderResult.paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Completar pago
                      </a>
                    </Button>
                  </div>
                )} */}
              </CardContent>
            </Card>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#FEFDFD] pb-12">
      <div className="container mx-auto px-4">{renderContent()}</div>
    </div>
  );
};

export default HybridCheckout;
